// Cave84 — SuggestInput
// Free-text input with a suggestion dropdown drawn from values already in the
// catalog. Unlike InlineCombobox (which selects an entity by id), this commits
// the raw string — used for free-text fields like Region / Subregion where we
// want to nudge toward existing values (avoid "Rhône" vs "Rhone" duplicates)
// without ever blocking a brand-new value (e.g. an Australian GI).

import { useRef, useState } from 'react';

interface SuggestInputProps {
  value: string;
  onValueChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  maxResults?: number;
}

const FIELD_CLS =
  'w-full ps-3 pe-3 py-2 rounded-md border border-border-medium bg-surface-1 text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors';

export function SuggestInput({
  value,
  onValueChange,
  suggestions,
  placeholder,
  maxResults = 8,
}: SuggestInputProps) {
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const term = value.trim().toLowerCase();
  const matches = suggestions
    .filter(s => {
      const lower = s.toLowerCase();
      return lower !== term && (term.length === 0 || lower.includes(term));
    })
    .slice(0, maxResults);
  const showList = open && matches.length > 0;

  const openNow = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={e => { onValueChange(e.target.value); openNow(); }}
        onFocus={openNow}
        onBlur={closeSoon}
        className={FIELD_CLS}
      />
      {showList && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border-medium bg-surface-1 shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
          {matches.map(s => (
            <button
              key={s}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onValueChange(s); setOpen(false); }}
              className="w-full text-start px-3 py-2 text-sm text-text-primary hover:bg-surface-2 border-b border-border-subtle last:border-0 transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
